import type { CashPositionReport, ReportBlock } from './cashPositionReport'

/** One printed page: a slice of the report's columns and blocks. */
export interface ReportPage {
  /** Indices into `report.columns`; the label column is printed on every page in addition. */
  columns: number[]
  /** Indices into `report.blocks`. */
  blocks: number[]
  /** The first row slice of its column slice — the page that prints the "as of" header line. */
  first: boolean
}

export interface ReportPageBudget {
  /** Data columns that fit across one page, not counting the label column. */
  maxDataColumns: number
  /** Table body rows that fit down one page; a section heading counts as a row. */
  maxRows: number
}

/** Blocks that are period-independent and always open the first row slice. */
const LEADING_BLOCKS: ReadonlySet<ReportBlock['key']> = new Set(['balances', 'loc'])

/**
 * Splits the report into printable pages, ordered down then across: every row
 * slice of the first column slice, then of the second, and so on.
 *
 * Columns: consecutive columns with the same `groupKey` (a pack's members and its
 * subtotal) stay on one page; a group wider than a page on its own starts a new
 * page and is split there. Rows: balances and LOC always open the first slice,
 * then whole period blocks are added while they fit. A slice that is too tall
 * anyway (one block taller than a page) is left to the writer to continue.
 */
export function paginateCashPositionReport(
  report: CashPositionReport,
  budget: ReportPageBudget,
): ReportPage[] {
  if (budget.maxDataColumns < 1 || budget.maxRows < 1) {
    throw new Error(`Report page budget must be positive: ${JSON.stringify(budget)}`)
  }
  const columnChunks = chunkColumns(report, budget.maxDataColumns)
  const rowChunks = chunkBlocks(report.blocks, budget.maxRows)
  return columnChunks.flatMap((columns) =>
    rowChunks.map((blocks, i) => ({ columns, blocks, first: i === 0 })),
  )
}

function chunkColumns(report: CashPositionReport, maxColumns: number): number[][] {
  const groups: number[][] = []
  report.columns.forEach((column, index) => {
    const last = groups.at(-1)
    if (last && report.columns[last[0]].groupKey === column.groupKey) last.push(index)
    else groups.push([index])
  })

  const chunks: number[][] = []
  let current: number[] = []
  for (const group of groups) {
    if (current.length + group.length <= maxColumns) {
      current.push(...group)
      continue
    }
    if (current.length > 0) chunks.push(current)
    current = []
    // Only a group that cannot fit even on an empty page is split.
    for (const index of group) {
      if (current.length === maxColumns) {
        chunks.push(current)
        current = []
      }
      current.push(index)
    }
  }
  if (current.length > 0 || chunks.length === 0) chunks.push(current)
  return chunks
}

function chunkBlocks(blocks: ReportBlock[], maxRows: number): number[][] {
  const leading: number[] = []
  const periods: number[] = []
  blocks.forEach((block, index) => (LEADING_BLOCKS.has(block.key) ? leading : periods).push(index))

  const chunks: number[][] = []
  let current = leading
  let rows = leading.reduce((sum, index) => sum + blocks[index].rows.length, 0)
  for (const index of periods) {
    const height = blocks[index].rows.length
    if (current.length > 0 && rows + height > maxRows) {
      chunks.push(current)
      current = []
      rows = 0
    }
    current.push(index)
    rows += height
  }
  if (current.length > 0 || chunks.length === 0) chunks.push(current)
  return chunks
}
